/**
 * HexSiteView — one SITE's panel: a framed board carrying that site's groups.
 *
 * The canvas has two levels of framing on purpose. Groups alone were enough while a board
 * showed one site, but the moment two sites' groups flowed into the same wrapping row there
 * was nothing on screen saying where one site ended and the next began — "Perimeter" at
 * Pune and "Perimeter" at Nashik sat side by side as if they were one place. The panel is
 * that boundary, and its header carries the only two facts worth repeating per site: how
 * big it is, and whether anything in it is lit up.
 */
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import HexClusterView from './HexClusterView';
import { SITE_HEADER_H, SITE_PAD, type LaidOutSite, type PositionedHex } from './hexLayout';
import { summarize, summaryBadge } from './hexSummary';
import type { HexColorState } from './types';
import type { HexCamera, HexCounter } from '../../api/contracts';

interface HexSiteViewProps {
  site: LaidOutSite<HexCamera>;
  selectedId: string | null;
  colorStateFor: (camera: HexCamera) => HexColorState;
  counterFor: (camera: HexCamera) => HexCounter | undefined;
  liveFor: (camera: HexCamera) => boolean;
  recordingFor: (camera: HexCamera) => boolean;
  isDimmed?: (camera: HexCamera) => boolean;
  onSelect: (hex: PositionedHex<HexCamera>) => void;
  onHoverStart: (hex: PositionedHex<HexCamera>) => void;
  onHoverEnd: () => void;
}

export default function HexSiteView({
  site,
  selectedId,
  colorStateFor,
  counterFor,
  liveFor,
  recordingFor,
  isDimmed,
  onSelect,
  onHoverStart,
  onHoverEnd,
}: HexSiteViewProps) {
  const cameras = site.clusters.flatMap((c) => c.hexes.map((h) => h.item));
  const badge = summaryBadge(summarize(cameras, { colorStateFor, counterFor }));

  return (
    <>
      {/* The panel frame exists only to separate one site from the NEXT one. A single-site
          board (no name — see SiteInput) has no next one, so it draws nothing: the groups sit
          straight on the canvas card the page already gives them, instead of a second box
          inside the first that frames the entire contents of the page.
          Where it is drawn it sits BEHIND the groups (which are absolutely positioned
          siblings in canvas space, not children), so it must not swallow clicks meant for a
          hex. */}
      {site.name && (
        <div
          className="pointer-events-none absolute rounded-2xl border border-border bg-muted/25"
          style={{ left: site.originX, top: site.originY, width: site.width, height: site.height }}
        />
      )}
      {/* No name = no site level on this board (one site — see SiteInput). The panel is then
          purely the common background the groups share, and a header naming the only place
          there is would be a line of chrome that tells the operator nothing. */}
      {site.name && (
        <div
          className="absolute flex select-none items-center gap-2"
          style={{
            left: site.originX + SITE_PAD,
            top: site.originY + SITE_PAD,
            width: site.width - SITE_PAD * 2,
            height: SITE_HEADER_H,
          }}
        >
          <BuildingOffice2Icon className="h-4 w-4 flex-none text-muted-foreground" />
          <span className="truncate text-sm font-bold text-foreground">{site.name}</span>
          <span className={`flex-none rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.className}`}>
            {badge.label}
          </span>
          <span className="flex-none text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            {site.cameraCount} camera{site.cameraCount === 1 ? '' : 's'}
          </span>
        </div>
      )}

      {site.clusters.map((cluster) => (
        <HexClusterView
          key={cluster.id}
          cluster={cluster}
          selectedId={selectedId}
          colorStateFor={colorStateFor}
          counterFor={counterFor}
          liveFor={liveFor}
          recordingFor={recordingFor}
          isDimmed={isDimmed}
          onSelect={onSelect}
          onHoverStart={onHoverStart}
          onHoverEnd={onHoverEnd}
        />
      ))}
    </>
  );
}
